import * as NextSkyTheme from "@NextSkyTheme/global";

const strings = window.byobCartGuardStrings || {
  checkoutBlocked:
    "Some products in your Build Your Own Box are no longer available. Please remove the box from your cart and build again.",
  checkoutBlockedSingle: "{{ product }} in your Build Your Own Box has unfortunately sold out.",
  checkoutBlockedMultiple:
    "These products in your box are no longer in stock: {{ products }}. Please remove the box from your cart and build again.",
  checkoutFollowUp: "Please edit your box or remove it from your cart.",
  modalTitle: "Product no longer available",
  modalClose: "Close",
};

const EDIT_DRAFT_KEY = "byob-edit-draft";
const SHELL_TITLE_PATTERN = /medium one|large one|x-large one/i;

const PUBLIC_BYOB_PROPERTY_KEYS = new Set([
  "byob_travel_ids",
  "byob_full_ids",
  "byob_role",
  "byob_variant_ids",
  "byob_design_variant_id",
]);

let validationPromise = null;
let inventoryPayloadCache = null;
let inventoryPayloadLoaded = false;
let inventoryRefreshPromise = null;
let inventoryRefreshedAt = 0;
const INVENTORY_REFRESH_MS = 10000;
const INVENTORY_SECTION_ID = "byob-inventory-data";
let cachedValidation = { issues: [], items: [] };

function decodeSkuMap(raw) {
  const map = {};

  for (const part of String(raw || "").split(",")) {
    const separator = part.indexOf(":");
    if (separator <= 0) continue;

    const sku = part.slice(0, separator).trim();
    const variantId = part.slice(separator + 1).trim();
    if (sku && variantId) map[sku] = variantId;
  }

  return map;
}

function expandShopifyImageUrl(path) {
  const normalized = String(path || "").trim();
  if (!normalized) return "";
  if (/^https?:\/\//i.test(normalized)) return normalized;
  return `https://cdn.shopify.com/${normalized.replace(/^\/+/, "")}`;
}

function decodeImageMap(raw) {
  const map = {};

  for (const part of String(raw || "").split(",")) {
    const separator = part.indexOf("|");
    if (separator <= 0) continue;

    const variantId = part.slice(0, separator).trim();
    const imagePath = part.slice(separator + 1).trim();
    if (!variantId || !imagePath) continue;

    map[variantId] = expandShopifyImageUrl(imagePath);
  }

  return map;
}

function decodeInventoryChunk(chunk) {
  if (!chunk) return {};

  if (chunk.variants && typeof chunk.variants === "object") {
    return chunk.variants;
  }

  if (typeof chunk.v !== "string" || !chunk.v.length) {
    return {};
  }

  const variants = {};

  for (const part of chunk.v.split(",")) {
    const separator = part.lastIndexOf(":");
    if (separator <= 0) continue;

    const variantId = part.slice(0, separator).trim();
    const quantity = Number(part.slice(separator + 1));

    if (variantId) {
      variants[variantId] = Number.isFinite(quantity) ? quantity : 0;
    }
  }

  return variants;
}

function getInventoryPayload() {
  if (inventoryPayloadLoaded) return inventoryPayloadCache;

  inventoryPayloadLoaded = true;

  try {
    const node = document.getElementById("byob-inventory-index");
    if (!node?.textContent?.trim()) {
      inventoryPayloadCache = { variants: null, skuToVariantId: {}, imageByVariantId: {} };
      return inventoryPayloadCache;
    }

    const parsed = JSON.parse(node.textContent);
    const variants = decodeInventoryChunk(parsed);
    inventoryPayloadCache = {
      variants: Object.keys(variants).length ? variants : null,
      skuToVariantId: decodeSkuMap(parsed.s),
      imageByVariantId: decodeImageMap(parsed.m),
    };
  } catch {
    inventoryPayloadCache = { variants: null, skuToVariantId: {}, imageByVariantId: {} };
  }

  return inventoryPayloadCache;
}

function getInventoryIndex() {
  return getInventoryPayload()?.variants || null;
}

function getInventorySkuMap() {
  return getInventoryPayload()?.skuToVariantId || {};
}

function resetInventoryPayloadCache() {
  inventoryPayloadLoaded = false;
  inventoryPayloadCache = null;
}

async function refreshInventoryIndex({ force = false } = {}) {
  const now = Date.now();
  if (!force && now - inventoryRefreshedAt < INVENTORY_REFRESH_MS) {
    return false;
  }

  if (inventoryRefreshPromise) return inventoryRefreshPromise;

  inventoryRefreshPromise = (async () => {
    try {
      const root = getRoot().replace(/\/?$/, "/");
      const response = await fetch(`${root}?sections=${INVENTORY_SECTION_ID}`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!response.ok) return false;

      const data = await response.json();
      const html = data[INVENTORY_SECTION_ID];
      if (!html) return false;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const freshNode = doc.getElementById("byob-inventory-index");
      if (!freshNode?.textContent?.trim()) return false;

      let target = document.getElementById("byob-inventory-index");
      if (!target) {
        target = document.createElement("script");
        target.type = "application/json";
        target.id = "byob-inventory-index";
        document.body.appendChild(target);
      }

      target.textContent = freshNode.textContent;
      resetInventoryPayloadCache();
      inventoryRefreshedAt = Date.now();
      return true;
    } catch {
      return false;
    } finally {
      inventoryRefreshPromise = null;
    }
  })();

  return inventoryRefreshPromise;
}

function getInventoryImageMap() {
  return getInventoryPayload()?.imageByVariantId || {};
}

function lookupSyncedImage(variantId, sku = "") {
  const images = getInventoryImageMap();
  const id = normalizeVariantId(variantId);
  if (id && images[id]) return normalizeImageUrl(images[id]);

  const normalizedSku = String(sku || "").trim();
  if (normalizedSku) {
    const mappedId = normalizeVariantId(getInventorySkuMap()[normalizedSku]);
    if (mappedId && images[mappedId]) return normalizeImageUrl(images[mappedId]);
  }

  return "";
}

function lookupSyncedQuantity(variantId) {
  const id = normalizeVariantId(variantId);
  if (!id) return undefined;

  const index = getInventoryIndex();
  if (!index || !(id in index)) return undefined;

  return index[id];
}

function resolveComponentVariantId(component, shellSkuMap = {}) {
  const directId = normalizeVariantId(component?.id);
  if (directId && lookupSyncedQuantity(directId) !== undefined) {
    return directId;
  }

  const sku = String(component?.sku || shellSkuMap[directId] || "").trim();
  if (sku) {
    const mappedId = normalizeVariantId(getInventorySkuMap()[sku]);
    if (mappedId) return mappedId;
  }

  return directId || normalizeVariantId(getInventorySkuMap()[sku]);
}

function hasInsufficientSyncedStock(variantId, requestedQuantity) {
  const synced = lookupSyncedQuantity(variantId);
  if (synced === undefined || synced === -1) return false;
  return synced < requestedQuantity;
}

function normalizeImageUrl(value) {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]") return "";
    return trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;
  }

  return normalizeImageUrl(value.src || value.url || "");
}

function getRoot() {
  return window.Shopify?.routes?.root || "/";
}

async function fetchCart() {
  const response = await fetch(`${getRoot()}cart.js`);
  if (!response.ok) throw new Error("Failed to load cart");
  return response.json();
}

function getProductImageUrl(product) {
  if (!product) return "";

  const featured = product.featured_image;
  const fromFeatured = normalizeImageUrl(featured);
  if (fromFeatured) return fromFeatured;

  const firstImage = product.images?.[0];
  return normalizeImageUrl(firstImage);
}

async function fetchProductByHandle(handle) {
  const slug = String(handle || "").trim();
  if (!slug) return null;

  const response = await fetch(`${getRoot()}products/${encodeURIComponent(slug)}.js`);
  if (!response.ok) return null;
  return response.json();
}

async function fetchProductHandleFromSearch(query) {
  const needle = String(query || "").trim();
  if (!needle) return "";

  try {
    const response = await fetch(
      `${getRoot()}search/suggest.json?q=${encodeURIComponent(needle)}&resources[type]=product&resources[limit]=8&resources[options][fields]=variants.sku,title,product_type,vendor,tag`,
    );
    if (!response.ok) return "";

    const data = await response.json();
    const products = data.resources?.results?.products || [];
    return products.find((product) => product?.handle)?.handle || "";
  } catch {
    return "";
  }
}

function getVariantImageFromProduct(variant, product) {
  let imageUrl = normalizeImageUrl(getVariantImageUrl(variant));
  if (imageUrl) return imageUrl;
  if (!product) return "";

  const imageId = variant?.image_id;
  if (imageId && Array.isArray(product.images)) {
    const match = product.images.find((image) => image?.id === imageId);
    imageUrl = normalizeImageUrl(match);
    if (imageUrl) return imageUrl;
  }

  return getProductImageUrl(product);
}

async function fetchVariantAndProductBySku(sku, titleFallback = "") {
  const queries = [...new Set([String(sku || "").trim(), String(titleFallback || "").trim()].filter(Boolean))];

  for (const query of queries) {
    try {
      const response = await fetch(
        `${getRoot()}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][fields]=variants.sku,title,product_type,vendor,tag`,
      );
      if (!response.ok) continue;

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      for (const product of products) {
        if (!product?.handle) continue;

        const productResponse = await fetch(`${getRoot()}products/${product.handle}.js`);
        if (!productResponse.ok) continue;

        const productData = await productResponse.json();
        const needle = String(sku || "").trim();
        const variant = needle
          ? productData.variants?.find((entry) => String(entry.sku || "").trim() === needle)
          : productData.variants?.[0];

        if (!variant) continue;

        return {
          variant,
          product: productData,
          imageUrl: getVariantImageFromProduct(variant, productData),
        };
      }
    } catch {
      // Try the next query.
    }
  }

  return null;
}

async function resolveIssueImage(issue) {
  const fromIndex = lookupSyncedImage(issue?.variantId, issue?.sku);
  if (fromIndex) return fromIndex;

  const existing = normalizeImageUrl(issue?.imageUrl);
  if (existing) return existing;

  const sku = String(issue?.sku || "").trim();
  const title = String(issue?.title || "").trim();
  const bySku = await fetchVariantAndProductBySku(sku, title);
  if (bySku?.imageUrl) return bySku.imageUrl;

  const variantId = normalizeVariantId(issue?.variantId);
  if (!variantId) return "";

  const variant = await fetchVariant(variantId);
  if (!variant) return "";

  let imageUrl = normalizeImageUrl(getVariantImageUrl(variant));
  if (imageUrl) return imageUrl;

  const handle = await fetchProductHandleFromSearch(variant.name || title || sku);
  if (!handle) return "";

  const product = await fetchProductByHandle(handle);
  return getVariantImageFromProduct(variant, product);
}

async function fetchVariantMetadata(ids) {
  const uniqueIds = [
    ...new Set((ids || []).map((id) => normalizeVariantId(id)).filter(Boolean)),
  ];
  if (!uniqueIds.length) return { names: {}, images: {} };

  const entries = await Promise.all(
    uniqueIds.map(async (variantId) => {
      const variant = await fetchVariant(variantId);
      if (!variant) return null;

      const title = variant.name || variant.public_title || variant.title;
      let imageUrl = normalizeImageUrl(getVariantImageUrl(variant));

      if (!imageUrl) {
        const handle = await fetchProductHandleFromSearch(variant.name || variant.sku || "");
        if (handle) {
          const product = await fetchProductByHandle(handle);
          imageUrl = getVariantImageFromProduct(variant, product);
        }
      }

      return {
        variantId,
        title: title ? String(title) : "",
        imageUrl,
      };
    }),
  );

  const names = {};
  const images = {};

  entries.filter(Boolean).forEach((entry) => {
    if (entry.title) names[entry.variantId] = entry.title;
    if (entry.imageUrl) images[entry.variantId] = entry.imageUrl;
  });

  return { names, images };
}

async function fetchNamesForVariantIds(ids) {
  const { names } = await fetchVariantMetadata(ids);
  return names;
}

function buildSkuTitleMapFromShellProps(props) {
  const map = {};

  Object.entries(props || {}).forEach(([label]) => {
    if (label.startsWith("_") || label.toLowerCase().startsWith("byob_")) return;

    const skuMatch = String(label).match(/\(([^)]+)\)\s*$/);
    if (!skuMatch) return;

    const title = String(label)
      .replace(/\s*\([^)]+\)\s*$/, "")
      .trim();
    if (!title) return;

    map[skuMatch[1].trim()] = title;
  });

  return map;
}

async function buildByobValidationBoxesWithNames(cartItems) {
  const shells = (cartItems || []).filter(isByobShell);
  const boxes = [];

  for (const item of shells) {
    const props = item.properties || {};
    const ids = [
      ...parseVariantIdCounts(props._byob_travel_ids).map((entry) => entry.id),
      ...parseVariantIdCounts(props._byob_full_ids).map((entry) => entry.id),
    ];
    if (props._byob_design_variant_id) {
      const designId = normalizeVariantId(props._byob_design_variant_id);
      if (designId) ids.push(designId);
    }

    const { names, images } = await fetchVariantMetadata(ids);
    Object.entries(buildSkuTitleMapFromShellProps(props)).forEach(([sku, title]) => {
      names[`sku:${sku}`] = title;
    });

    boxes.push({
      t: props._byob_travel_ids || "",
      f: props._byob_full_ids || "",
      v: props._byob_variant_ids || "",
      d: props._byob_design_variant_id || "",
      ...(Object.keys(names).length ? { n: names } : {}),
      ...(Object.keys(images).length ? { i: images } : {}),
    });
  }

  return boxes;
}

async function syncByobValidationCartAttribute(cartItems) {
  const boxes = await buildByobValidationBoxesWithNames(cartItems);
  const response = await fetch(`${getRoot().replace(/\/?$/, "/")}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      attributes: {
        byob_active: boxes.length ? "1" : "",
        byob_validation: boxes.length ? JSON.stringify(boxes) : "",
        _byob_validation: "",
      },
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to sync BYOB validation cart attribute");
  }

  return response.json();
}

function shellHasPublicByobProperties(props) {
  return Object.keys(props || {}).some((key) => {
    if (key.startsWith("_")) return false;
    return key.startsWith("byob_") || PUBLIC_BYOB_PROPERTY_KEYS.has(key);
  });
}

function getCleanShellProperties(props) {
  const clean = {};

  Object.entries(props || {}).forEach(([key, value]) => {
    if (key.startsWith("_")) {
      clean[key] = value;
      return;
    }

    if (key.startsWith("byob_")) return;

    clean[key] = value;
  });

  return clean;
}

async function sanitizePublicByobLineProperties() {
  const changeUrl = window.routes?.cart_change_url;
  if (!changeUrl) return false;

  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return false;
  }

  const shells = (cart.items || [])
    .filter(isByobShell)
    .filter((item) => shellHasPublicByobProperties(item.properties || {}));

  if (!shells.length) return false;

  for (const item of shells) {
    const config = NextSkyTheme.fetchConfig("json");
    config.body = JSON.stringify({
      id: item.key,
      quantity: item.quantity,
      properties: getCleanShellProperties(item.properties),
    });

    const response = await fetch(changeUrl, config);
    const data = await response.json();
    if (data?.status) return false;
  }

  if (NextSkyTheme?.publish && NextSkyTheme.PUB_SUB_EVENTS?.cartUpdate) {
    NextSkyTheme.publish(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, {
      source: "byob-property-sanitize",
    });
  }

  document.dispatchEvent(new CustomEvent("byob:cart:sanitized"));

  return true;
}

function hidePublicByobCartPropertyNodes() {
  document.querySelectorAll(".product_option-cart .product-option").forEach((node) => {
    const label = node.querySelector("span");
    const text = String(label?.textContent || "")
      .trim()
      .replace(/:$/, "")
      .toLowerCase();

    if (text.startsWith("byob_") || PUBLIC_BYOB_PROPERTY_KEYS.has(text)) {
      node.remove();
    }
  });
}

async function fetchVariant(variantId) {
  const id = normalizeVariantId(variantId);
  if (!id) return null;

  const response = await fetch(`${getRoot()}variants/${id}.js`);
  if (!response.ok) return null;
  return response.json();
}

async function fetchVariantBySku(sku, titleFallback = "") {
  const result = await fetchVariantAndProductBySku(sku, titleFallback);
  return result?.variant || null;
}

function isByobShell(item) {
  const props = item.properties || {};
  if (props._byob_role === "component" || props._byob_role === "gift") return false;
  if (props._byob_role === "shell") return true;
  if (props._byob_variant_ids || props._byob_box_key || props._byob_box_id) return true;

  const title = `${item.product_title || ""} ${item.title || ""}`;
  const visible = Object.entries(props).filter(
    ([key]) => !key.startsWith("_") && !key.startsWith("byob_"),
  );
  const hasPickerLines = visible.some(
    ([label, value]) => label.includes("(") && String(value).includes("x"),
  );

  if (hasPickerLines && visible.length >= 2) return true;
  if (SHELL_TITLE_PATTERN.test(title) && visible.length >= 2) return true;

  return false;
}

function getValidationMaps(cart) {
  try {
    const raw = cart?.attributes?.byob_validation;
    if (!raw) return { names: {}, images: {} };

    const boxes = JSON.parse(raw);
    const names = {};
    const images = {};

    (Array.isArray(boxes) ? boxes : [boxes]).forEach((box) => {
      if (box?.n && typeof box.n === "object") {
        Object.assign(names, box.n);
      }
      if (box?.i && typeof box.i === "object") {
        Object.assign(images, box.i);
      }
    });

    return { names, images };
  } catch {
    return { names: {}, images: {} };
  }
}

function getValidationNameMap(cart) {
  return getValidationMaps(cart).names;
}

function getValidationImageMap(cart) {
  return getValidationMaps(cart).images;
}

function buildVisibleProductEntriesWithQty(props) {
  const entries = [];

  Object.entries(props || {}).forEach(([label, value]) => {
    if (label.startsWith("_") || label.toLowerCase().startsWith("byob_")) return;

    const skuMatch = String(label).match(/\(([^)]+)\)\s*$/);
    if (!skuMatch) return;

    const qtyMatch = String(value).match(/x(\d+)/i);
    const quantity = qtyMatch ? Math.max(1, Number(qtyMatch[1]) || 1) : 1;

    entries.push({
      title: String(label)
        .replace(/\s*\([^)]+\)\s*$/, "")
        .trim(),
      sku: skuMatch[1].trim(),
      quantity,
    });
  });

  return entries;
}

function getDesignSkuFromProps(props) {
  const designId = getDesignVariantId(props);
  if (!designId) return "";

  const entries = buildVisibleProductEntriesWithQty(props);
  const slotCount = buildShellVariantIdOrder(props).length;
  if (slotCount > 0 && entries.length > slotCount) {
    return entries[slotCount]?.sku || "";
  }

  return "";
}

function getProductVisibleEntriesWithQty(props) {
  const designSku = getDesignSkuFromProps(props);
  return buildVisibleProductEntriesWithQty(props).filter(
    (entry) => !designSku || entry.sku !== designSku,
  );
}

function buildShellVariantIdToSkuMap(props) {
  const variantSlots = buildShellVariantIdOrder(props);
  const entries = getProductVisibleEntriesWithQty(props);
  const skuSequence = [];

  entries.forEach((entry) => {
    for (let index = 0; index < entry.quantity; index += 1) {
      skuSequence.push(entry.sku);
    }
  });

  const map = {};
  variantSlots.forEach((variantId, index) => {
    const sku = skuSequence[index];
    if (sku) {
      map[String(variantId)] = sku;
    }
  });

  return map;
}

function buildVisibleProductEntries(props) {
  const entries = [];

  Object.entries(props || {}).forEach(([label]) => {
    if (label.startsWith("_") || label.toLowerCase().startsWith("byob_")) return;

    const skuMatch = String(label).match(/\(([^)]+)\)\s*$/);
    if (!skuMatch) return;

    entries.push({
      title: String(label)
        .replace(/\s*\([^)]+\)\s*$/, "")
        .trim(),
      sku: skuMatch[1].trim(),
    });
  });

  return entries;
}

function buildShellVariantIdOrder(props) {
  const travel = parseVariantIdCounts(props._byob_travel_ids).flatMap((entry) =>
    Array.from({ length: entry.quantity }, () => entry.id),
  );
  const full = parseVariantIdCounts(props._byob_full_ids).flatMap((entry) =>
    Array.from({ length: entry.quantity }, () => entry.id),
  );

  return [...travel, ...full];
}

function getDesignVariantId(props) {
  return normalizeVariantId(props?._byob_design_variant_id);
}

function getProductVisibleEntries(props) {
  const entries = buildVisibleProductEntries(props);
  const designId = getDesignVariantId(props);
  if (!designId) return entries;

  const slotCount = buildShellVariantIdOrder(props).length;
  if (slotCount > 0 && entries.length > slotCount) {
    return entries.slice(0, slotCount);
  }

  const designSku = getDesignSkuFromProps(props);
  if (designSku) {
    return entries.filter((entry) => entry.sku !== designSku);
  }

  return entries;
}

function buildShellVariantSkuMap(shell) {
  const props = shell?.properties || {};
  const idToSku = buildShellVariantIdToSkuMap(props);
  const map = { ...idToSku };

  getProductVisibleEntries(props).forEach((entry) => {
    if (entry.sku) {
      map[`sku:${entry.sku}`] = entry.sku;
    }
  });

  return map;
}

function buildShellVariantTitleMap(shell) {
  const props = shell?.properties || {};
  const skuToTitle = buildSkuTitleMapFromShellProps(props);
  const idToSku = buildShellVariantIdToSkuMap(props);
  const map = {};

  Object.entries(idToSku).forEach(([variantId, sku]) => {
    if (skuToTitle[sku]) {
      map[variantId] = skuToTitle[sku];
    }
  });

  Object.entries(skuToTitle).forEach(([sku, title]) => {
    map[`sku:${sku}`] = title;
  });

  return map;
}

function getTitleFromShellProperties(shell, variant) {
  const props = shell?.properties || {};
  const sku = String(variant?.sku || "").trim();

  if (sku) {
    for (const [label] of Object.entries(props)) {
      if (label.startsWith("_") || label.toLowerCase().startsWith("byob_")) continue;
      if (label.includes(`(${sku})`)) {
        return label.replace(/\s*\([^)]+\)\s*$/, "").trim();
      }
    }
  }

  return null;
}

function resolveComponentTitle(component, shell, variant, nameMap = {}, shellTitleMap = {}) {
  if (component?.title) return component.title;

  const variantId = normalizeVariantId(component?.id || variant?.id);
  if (variantId && shellTitleMap[variantId]) return shellTitleMap[variantId];

  const fromProps = getTitleFromShellProperties(shell, variant);
  if (fromProps) return fromProps;

  const sku = String(variant?.sku || "").trim();
  if (sku) {
    const skuTitles = buildSkuTitleMapFromShellProps(shell?.properties || {});
    if (skuTitles[sku]) return skuTitles[sku];
    if (shellTitleMap[`sku:${sku}`]) return shellTitleMap[`sku:${sku}`];
    if (nameMap[`sku:${sku}`]) return nameMap[`sku:${sku}`];
  }

  if (variantId && nameMap[variantId]) return nameMap[variantId];

  const rawName = variant?.name || variant?.public_title || variant?.title || "";
  if (rawName) {
    return String(rawName).replace(/\s*-\s*Default Title$/i, "").trim();
  }

  return "";
}

function getVariantImageUrl(variant) {
  if (!variant) return "";

  const featured = variant.featured_image;
  if (typeof featured === "string" && featured) return featured;

  return (
    featured?.src ||
    featured?.url ||
    variant?.image?.src ||
    variant?.image?.url ||
    (typeof variant?.image === "string" ? variant.image : "") ||
    ""
  );
}

async function fetchImageBySku(sku, titleFallback = "") {
  const result = await fetchVariantAndProductBySku(sku, titleFallback);
  if (result?.imageUrl) return result.imageUrl;

  const needle = String(sku || titleFallback || "").trim();
  if (!needle) return "";

  try {
    const response = await fetch(
      `${getRoot()}search/suggest.json?q=${encodeURIComponent(needle)}&resources[type]=product&resources[limit]=1&resources[options][fields]=variants.sku,title`,
    );
    if (!response.ok) return "";

    const data = await response.json();
    const product = data.resources?.results?.products?.[0];
    return normalizeImageUrl(product?.image || product?.featured_image);
  } catch {
    return "";
  }
}

async function resolveVariantImage(variantId, variant = null) {
  const record = variant || (await fetchVariant(variantId));
  let imageUrl = normalizeImageUrl(getVariantImageUrl(record));
  if (imageUrl) return imageUrl;

  const bySku = await fetchVariantAndProductBySku(record?.sku, record?.name || record?.title);
  if (bySku?.imageUrl) return bySku.imageUrl;

  const handle = await fetchProductHandleFromSearch(record?.name || record?.sku || "");
  if (!handle) return "";

  const product = await fetchProductByHandle(handle);
  return getVariantImageFromProduct(record, product);
}

function normalizeVariantId(value) {
  const id = String(value ?? "").trim();
  return /^\d+$/.test(id) ? id : "";
}

function parseVariantIdCounts(value) {
  const counts = new Map();

  String(value || "")
    .split(",")
    .map((id) => normalizeVariantId(id))
    .filter(Boolean)
    .forEach((id) => {
      counts.set(id, (counts.get(id) || 0) + 1);
    });

  return [...counts.entries()].map(([id, quantity]) => ({
    id,
    quantity,
    title: null,
  }));
}

function getByobSkuComponents(shellItem, cartItems) {
  const props = shellItem.properties || {};
  const nested = cartItems
    .filter((item) => {
      const itemProps = item.properties || {};
      const boxId = props._byob_box_id;
      return (
        itemProps._byob_role === "component" &&
        itemProps._byob_component_type !== "design" &&
        (!boxId || itemProps._byob_box_id === boxId)
      );
    })
    .map((item) => ({
      id: normalizeVariantId(item.variant_id),
      sku: "",
      quantity: item.quantity,
      title: item.product_title,
      imageUrl: item.featured_image?.url || item.image || "",
    }))
    .filter((item) => item.id);

  if (nested.length) return nested;

  const idToSku = buildShellVariantIdToSkuMap(props);
  const skuToVariantId = {};
  Object.entries(idToSku).forEach(([variantId, sku]) => {
    if (sku && !skuToVariantId[sku]) {
      skuToVariantId[sku] = variantId;
    }
  });

  return getProductVisibleEntriesWithQty(props).map((entry) => ({
    id: skuToVariantId[entry.sku] || "",
    sku: entry.sku,
    quantity: entry.quantity,
    title: entry.title,
    imageUrl: "",
  }));
}

function getByobComponents(shellItem, cartItems) {
  return getByobSkuComponents(shellItem, cartItems);
}

function variantHasSufficientInventory(variant, requestedQuantity, variantId = "") {
  const id = normalizeVariantId(variantId || variant?.id);
  const synced = lookupSyncedQuantity(id);
  if (synced !== undefined && synced !== -1) {
    return synced >= requestedQuantity;
  }

  if (!variant) return true;

  if (variant.available === false) return false;

  const available = Number(variant.inventory_quantity);
  if (variant.inventory_management) {
    if (!Number.isFinite(available)) return true;
    return available >= requestedQuantity;
  }

  if (Number.isFinite(available) && available < requestedQuantity) {
    return false;
  }

  return true;
}

async function validateVariantAvailability(
  component,
  shell,
  nameMap = {},
  shellTitleMap = {},
  shellSkuMap = {},
  imageMap = {},
) {
  const variantId = normalizeVariantId(component.id);
  const mappedSku = String(component.sku || (variantId ? shellSkuMap[variantId] : "") || "").trim();
  let variant = null;

  if (mappedSku) {
    variant = await fetchVariantBySku(mappedSku, component.title || "");
  }

  if (!variant && variantId) {
    variant = await fetchVariant(variantId);
  }

  const resolvedVariantId = normalizeVariantId(variant?.id) || variantId;
  const resolvedSku = String(variant?.sku || mappedSku || "").trim();
  const title =
    resolveComponentTitle(component, shell, variant, nameMap, shellTitleMap) ||
    (resolvedSku ? buildSkuTitleMapFromShellProps(shell?.properties || {})[resolvedSku] : "") ||
    component.title ||
    (resolvedSku ? resolvedSku : resolvedVariantId ? `Product #${resolvedVariantId}` : "Product");

  let imageUrl =
    lookupSyncedImage(resolvedVariantId, resolvedSku) ||
    component.imageUrl ||
    (resolvedVariantId ? imageMap[resolvedVariantId] : "") ||
    (resolvedSku ? imageMap[`sku:${resolvedSku}`] : "");

  if (!imageUrl && variant) {
    imageUrl = await resolveVariantImage(resolvedVariantId || variant.id, variant);
  }

  if (!imageUrl && resolvedSku) {
    imageUrl = await fetchImageBySku(resolvedSku, title);
  }

  if (!imageUrl && title) {
    imageUrl = await fetchImageBySku(title, title);
  }

  imageUrl = normalizeImageUrl(imageUrl);

  if (hasInsufficientSyncedStock(resolvedVariantId, component.quantity)) {
    const synced = lookupSyncedQuantity(resolvedVariantId);
    return {
      issue: true,
      variantId: resolvedVariantId,
      title,
      imageUrl,
      available: typeof synced === "number" && synced >= 0 ? synced : 0,
    };
  }

  if (!variant) {
    return { issue: false };
  }

  if (variantHasSufficientInventory(variant, component.quantity, resolvedVariantId)) {
    return { issue: false };
  }

  const available = Number(variant.inventory_quantity);
  return {
    issue: true,
    variantId: resolvedVariantId,
    title,
    imageUrl,
    available: Number.isFinite(available) ? available : 0,
  };
}

async function resolveComponentVariantIdAsync(component, shellSkuMap = {}) {
  let variantId = resolveComponentVariantId(component, shellSkuMap);
  if (variantId) return variantId;

  const sku = String(component?.sku || shellSkuMap[component?.id] || "").trim();
  if (!sku) return "";

  const variant = await fetchVariantBySku(sku, component?.title || "");
  return normalizeVariantId(variant?.id);
}

async function validateByobCartFast(cart) {
  const items = cart?.items || [];
  const shells = items.filter(isByobShell);
  if (!shells.length) return [];

  const { names: nameMap, images: imageMap } = getValidationMaps(cart);
  const issues = [];

  for (const shell of shells) {
    const shellTitleMap = buildShellVariantTitleMap(shell);
    const shellSkuMap = buildShellVariantSkuMap(shell);
    const components = getByobComponents(shell, items);
    const checked = new Set();

    for (const component of components) {
      const sku = String(component.sku || shellSkuMap[component.id] || "").trim();
      const componentKey = sku || String(component.id);
      if (!componentKey || checked.has(componentKey)) continue;
      checked.add(componentKey);

      const variantId = await resolveComponentVariantIdAsync(component, shellSkuMap);
      if (!variantId || !hasInsufficientSyncedStock(variantId, component.quantity)) continue;

      const synced = lookupSyncedQuantity(variantId);
      const title =
        component.title ||
        resolveComponentTitle(component, shell, null, nameMap, shellTitleMap) ||
        (sku ? buildSkuTitleMapFromShellProps(shell?.properties || {})[sku] : "") ||
        sku ||
        `Product #${variantId}`;

      issues.push({
        shellKey: shell.key,
        variantId,
        sku,
        title,
        imageUrl: normalizeImageUrl(
          lookupSyncedImage(variantId, sku) ||
            component.imageUrl ||
            imageMap[variantId] ||
            (sku ? imageMap[`sku:${sku}`] : ""),
        ),
        available: typeof synced === "number" && synced >= 0 ? synced : 0,
      });
    }
  }

  return issues;
}

async function validateByobCart(cart) {
  const items = cart?.items || (await fetchCart()).items;
  const shells = items.filter(isByobShell);
  if (!shells.length) return [];

  const { names: nameMap, images: imageMap } = getValidationMaps(cart);
  const issueLists = await Promise.all(
    shells.map(async (shell) => {
      const shellTitleMap = buildShellVariantTitleMap(shell);
      const shellSkuMap = buildShellVariantSkuMap(shell);
      const components = getByobComponents(shell, items);
      const checkedVariantIds = new Set();
      const shellIssues = [];

      for (const component of components) {
        const componentKey = component.sku || String(component.id);
        if (!componentKey || checkedVariantIds.has(componentKey)) continue;
        checkedVariantIds.add(componentKey);

        const result = await validateVariantAvailability(
          component,
          shell,
          nameMap,
          shellTitleMap,
          shellSkuMap,
          imageMap,
        );

        if (result.issue) {
          shellIssues.push({
            shellKey: shell.key,
            variantId: result.variantId,
            sku: component.sku || shellSkuMap[component.id] || "",
            title: result.title,
            imageUrl: normalizeImageUrl(result.imageUrl),
            available: result.available,
          });
        }
      }

      return shellIssues;
    }),
  );

  return issueLists.flat();
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function getEditConfig() {
  return window.byobCartEditConfig || {};
}

function buildDraftFromShellItem(item) {
  const props = item.properties || {};
  return {
    lineKey: item.key,
    shellVariantId: item.variant_id,
    boxKey: props._byob_box_key || "",
    travelIds: parseVariantIdCounts(props._byob_travel_ids).map((entry) => entry.id),
    fullIds: parseVariantIdCounts(props._byob_full_ids).map((entry) => entry.id),
    designVariantId: props._byob_design_variant_id ? Number(props._byob_design_variant_id) : null,
    variantIds: parseVariantIdCounts(props._byob_variant_ids).map((entry) => entry.id),
    visibleProperties: Object.entries(props)
      .filter(([key]) => !key.startsWith("_") && !key.startsWith("byob_"))
      .map(([label, value]) => ({ label, value: String(value) })),
  };
}

function renderEditButton(shellKey, label) {
  if (!shellKey) return "";

  const builderUrl = resolveBuilderUrl(getEditConfig().builderUrl);
  return `<button type="button" class="byob-cart-edit-btn btn fs-small byob-cart-guard__edit" data-byob-edit-cart data-byob-edit-line-key="${escapeHtml(shellKey)}" data-byob-edit-builder-url="${escapeHtml(builderUrl)}">${escapeHtml(label)}</button>`;
}

function resolveBuilderUrl(explicit) {
  const value = String(explicit || getEditConfig().builderUrl || "").trim();
  const root = getRoot().replace(/\/?$/, "/");
  if (!value) return `${root}pages/byob-test`;
  if (value.startsWith("http")) return value;
  if (value.startsWith("/")) return value;
  return `${root}${value.replace(/^\/+/, "")}`;
}

function formatIssueMessageParts(issue) {
  const template =
    strings.checkoutBlockedSingle || "{{ product }} in your Build Your Own Box has unfortunately sold out.";
  const product = issue.title || "";
  const marker = "{{ product }}";
  const index = template.indexOf(marker);

  if (index === -1) {
    return { product, suffix: template };
  }

  return {
    product,
    suffix: template.slice(index + marker.length),
  };
}

function renderIssueMessageHtml(issue) {
  const parts = formatIssueMessageParts(issue);

  return `
    <p class="byob-cart-guard__message mb-0">
      <span class="byob-cart-guard__product-name">${escapeHtml(parts.product)}</span>
      <span class="byob-cart-guard__message-body">${escapeHtml(parts.suffix.trim())}</span>
    </p>
  `;
}

function renderIssueMediaHtml(issue) {
  const imageUrl = normalizeImageUrl(issue.imageUrl);
  if (imageUrl) {
    const alt = issue.title ? escapeHtml(issue.title) : "";
    return `<img src="${escapeHtml(imageUrl)}" alt="${alt}" class="byob-cart-guard__image" width="96" height="120" loading="lazy" decoding="async" onerror="this.hidden=true;this.nextElementSibling?.removeAttribute('hidden')"><div class="byob-cart-guard__image-fallback" aria-hidden="true" hidden></div>`;
  }

  return `<div class="byob-cart-guard__image-fallback" aria-hidden="true"></div>`;
}

function formatSingleIssueMessage(issue) {
  return String(strings.checkoutBlockedSingle || "").replace(/\{\{\s*product\s*\}\}/g, issue.title);
}

function formatIssuesMessage(issues) {
  if (!issues.length) return "";

  if (issues.length === 1) {
    return formatSingleIssueMessage(issues[0]);
  }

  const names = [...new Set(issues.map((issue) => issue.title))].join(", ");
  return String(strings.checkoutBlockedMultiple || "").replace(/\{\{\s*products\s*\}\}/g, names);
}

function renderSoldOutIssueRow(issue) {
  return `
    <div class="byob-cart-guard__item">
      <div class="byob-cart-guard__media rounded-10">${renderIssueMediaHtml(issue)}</div>
      <div class="byob-cart-guard__details">
        ${renderIssueMessageHtml(issue)}
      </div>
    </div>
  `;
}

function getAlertContainers() {
  return [
    document.getElementById("CartDrawer-ByobStockAlert"),
    document.getElementById("MainCart-ByobStockAlert"),
  ].filter(Boolean);
}

function restoreAlertIfNeeded() {
  if (!cachedValidation.issues.length) return;

  const needsRestore = getAlertContainers().some(
    (container) => container.hidden || !container.innerHTML.trim(),
  );

  if (needsRestore) {
    renderAlert(cachedValidation.issues, cachedValidation.items);
  }
}

function setCheckoutButtonsBlocked(blocked) {
  document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
    if (blocked) {
      button.dataset.byobBlocked = "true";
      button.disabled = true;
      button.classList.add("disabled");
      button.setAttribute("aria-disabled", "true");
      return;
    }

    if (button.dataset.byobBlocked !== "true") return;

    delete button.dataset.byobBlocked;
    const termsCheckbox = button
      .closest("form")
      ?.querySelector('input[name="terms_and_conditions"]');

    if (!termsCheckbox || termsCheckbox.checked) {
      button.disabled = false;
      button.classList.remove("disabled");
      button.removeAttribute("aria-disabled");
    }
  });
}

function renderAlert(issues, cartItems = []) {
  const shell = cartItems.find((item) => issues.some((issue) => issue.shellKey === item.key)) ||
    cartItems.find(isByobShell);
  const editLabel = getEditConfig().editLabel || "Edit box";
  const uniqueIssues = [];
  const seen = new Set();

  issues.forEach((issue) => {
    const key = String(issue.variantId || issue.title);
    if (seen.has(key)) return;
    seen.add(key);
    uniqueIssues.push(issue);
  });

  const editButton = shell?.key ? renderEditButton(shell.key, editLabel) : "";

  let bodyHtml = "";

  if (uniqueIssues.length === 1) {
    const issue = uniqueIssues[0];

    bodyHtml = `
      <div class="byob-cart-guard__item byob-cart-guard__item--single">
        <div class="byob-cart-guard__media rounded-10">${renderIssueMediaHtml(issue)}</div>
        <div class="byob-cart-guard__details">
          ${renderIssueMessageHtml(issue)}
          <p class="byob-cart-guard__follow-up mb-0">${escapeHtml(strings.checkoutFollowUp || "Please edit your box or remove it from your cart.")}</p>
        </div>
        ${editButton}
      </div>
    `;
  } else {
    const rows = uniqueIssues.map((issue) => renderSoldOutIssueRow(issue)).join("");
    bodyHtml = `
      <div class="byob-cart-guard__items">${rows}</div>
      <div class="byob-cart-guard__footer">
        <p class="byob-cart-guard__follow-up mb-0">${escapeHtml(strings.checkoutFollowUp || "Please edit your box or remove it from your cart.")}</p>
        ${editButton}
      </div>
    `;
  }

  const html = `
    <div class="byob-cart-guard__alert" role="alert">
      ${bodyHtml}
    </div>
  `;

  getAlertContainers().forEach((container) => {
    container.innerHTML = html;
    container.hidden = false;
  });

  setCheckoutButtonsBlocked(true);
}

function clearAlert() {
  getAlertContainers().forEach((container) => {
    container.innerHTML = "";
    container.hidden = true;
  });

  setCheckoutButtonsBlocked(false);
}

function getSoldOutModal() {
  return document.getElementById("ByobSoldOutModal");
}

function ensureSoldOutModal() {
  let modal = getSoldOutModal();
  if (modal) return modal;

  modal = document.createElement("div");
  modal.id = "ByobSoldOutModal";
  modal.className = "byob-soldout-modal";
  modal.hidden = true;
  modal.innerHTML = `
    <div class="byob-soldout-modal__backdrop" data-byob-modal-close></div>
    <div class="byob-soldout-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="ByobSoldOutModalTitle">
      <button type="button" class="byob-soldout-modal__close" data-byob-modal-close aria-label="${escapeHtml(strings.modalClose || "Close")}">&times;</button>
      <div class="byob-soldout-modal__media">
        <img class="byob-soldout-modal__image" alt="" width="240" height="300" hidden>
        <div class="byob-soldout-modal__image-fallback" aria-hidden="true"></div>
      </div>
      <h2 id="ByobSoldOutModalTitle" class="byob-soldout-modal__title h4 mb-10"></h2>
      <p class="byob-soldout-modal__product mb-10"></p>
      <p class="byob-soldout-modal__message mb-15"></p>
      <div class="byob-soldout-modal__actions flex gap-sp-3 wrap">
        <button type="button" class="byob-cart-edit-btn btn btn-primary byob-soldout-modal__edit" data-byob-edit-cart></button>
        <button type="button" class="byob-soldout-modal__dismiss btn btn-secondary" data-byob-modal-close></button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  return modal;
}

function closeSoldOutModal() {
  const modal = getSoldOutModal();
  if (!modal) return;

  modal.hidden = true;
  document.body.classList.remove("byob-soldout-modal-open");
}

function showSoldOutModal(issues, cartItems = []) {
  if (!issues.length) return;

  const modal = ensureSoldOutModal();
  const issue = issues[0];
  const shell =
    cartItems.find((item) => item.key === issue.shellKey) || cartItems.find(isByobShell);
  const editLabel = getEditConfig().editLabel || "Edit box";
  const message = formatIssuesMessage(issues) || strings.checkoutBlocked;

  modal.querySelector(".byob-soldout-modal__title").textContent = strings.modalTitle || "Product no longer available";
  modal.querySelector(".byob-soldout-modal__product").textContent = issue.title || "";
  modal.querySelector(".byob-soldout-modal__message").textContent = message;

  const image = modal.querySelector(".byob-soldout-modal__image");
  const fallback = modal.querySelector(".byob-soldout-modal__image-fallback");
  if (issue.imageUrl) {
    image.src = issue.imageUrl;
    image.alt = issue.title || "";
    image.hidden = false;
    fallback.hidden = true;
  } else {
    image.removeAttribute("src");
    image.hidden = true;
    fallback.hidden = false;
  }

  const editButton = modal.querySelector(".byob-soldout-modal__edit");
  editButton.textContent = editLabel;
  if (shell?.key) {
    editButton.dataset.byobEditLineKey = shell.key;
    editButton.dataset.byobEditBuilderUrl = resolveBuilderUrl(getEditConfig().builderUrl);
    editButton.hidden = false;
  } else {
    delete editButton.dataset.byobEditLineKey;
    delete editButton.dataset.byobEditBuilderUrl;
    editButton.hidden = true;
  }

  modal.querySelector(".byob-soldout-modal__dismiss").textContent = strings.modalClose || "Close";
  modal.hidden = false;
  document.body.classList.add("byob-soldout-modal-open");
  editButton.focus();
}

function handleModalDismissClick(event) {
  if (!event.target.closest("[data-byob-modal-close]")) return;
  event.preventDefault();
  closeSoldOutModal();
}

async function enrichAndUpdateAlert(issues, cartItems) {
  const enriched = await Promise.all(
    issues.map(async (issue) => {
      if (normalizeImageUrl(issue.imageUrl)) return issue;

      const imageUrl = await resolveIssueImage(issue);
      return imageUrl ? { ...issue, imageUrl } : issue;
    }),
  );

  if (!enriched.length) return;

  cachedValidation = { issues: enriched, items: cartItems };
  renderAlert(enriched, cartItems);
}

async function runBackgroundCartMaintenance(cart, quickIssues) {
  try {
    await sanitizePublicByobLineProperties();
    hidePublicByobCartPropertyNodes();

    const refreshedCart = await fetchCart();
    if (!refreshedCart.items.some(isByobShell)) {
      cachedValidation = { issues: [], items: [] };
      clearAlert();
      await syncByobValidationCartAttribute([]).catch(() => {});
      return;
    }

    await syncByobValidationCartAttribute(refreshedCart.items).catch(() => {});

    const fullIssues = await validateByobCart(refreshedCart);
    if (fullIssues.length) {
      cachedValidation = { issues: fullIssues, items: refreshedCart.items };
      renderAlert(fullIssues, refreshedCart.items);
      void enrichAndUpdateAlert(fullIssues, refreshedCart.items);
      return;
    }

    if (!quickIssues.length) {
      cachedValidation = { issues: [], items: [] };
      clearAlert();
    }
  } catch (error) {
    console.error(error);
  }
}

async function runValidation({ refreshInventory = true } = {}) {
  if (validationPromise) return validationPromise;

  validationPromise = (async () => {
    try {
      if (refreshInventory) {
        await refreshInventoryIndex({ force: true });
      }

      const cart = await fetchCart();
      if (!cart.items.some(isByobShell)) {
        cachedValidation = { issues: [], items: [] };
        clearAlert();
        void syncByobValidationCartAttribute([]).catch(() => {});
        return [];
      }

      const quickIssues = await validateByobCartFast(cart);

      if (quickIssues.length) {
        cachedValidation = { issues: quickIssues, items: cart.items };
        renderAlert(quickIssues, cart.items);
        void enrichAndUpdateAlert(quickIssues, cart.items);
      } else {
        cachedValidation = { issues: [], items: [] };
        clearAlert();
      }

      void runBackgroundCartMaintenance(cart, quickIssues);

      return quickIssues;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      validationPromise = null;
    }
  })();

  return validationPromise;
}

async function guardCheckout(event) {
  const trigger = event.target.closest(
    'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]',
  );
  if (!trigger || trigger.dataset.byobBlocked === "true") return;

  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }

  if (!cart.items.some(isByobShell)) return;

  const issues = await runValidation();
  if (!issues.length) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  showSoldOutModal(issues, cart.items);
}

function init() {
  hidePublicByobCartPropertyNodes();
  void runValidation();
  void sanitizePublicByobLineProperties().then(() => {
    hidePublicByobCartPropertyNodes();
    void runValidation();
  });

  document.addEventListener("click", handleModalDismissClick, true);
  document.addEventListener("click", guardCheckout, true);
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!form?.querySelector?.('button[name="checkout"], input[name="checkout"]')) return;
      void guardCheckout(event);
    },
    true,
  );

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      hidePublicByobCartPropertyNodes();
      void runValidation({ refreshInventory: true });
    }
  });

  if (NextSkyTheme?.subscribe && NextSkyTheme?.PUB_SUB_EVENTS) {
    NextSkyTheme.subscribe(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, () => {
      hidePublicByobCartPropertyNodes();
      window.setTimeout(() => {
        hidePublicByobCartPropertyNodes();
        void runValidation({ refreshInventory: true }).then(restoreAlertIfNeeded);
      }, 50);
    });
  }

  document.addEventListener("modal:opened", () => {
    const drawer = document.querySelector("cart-drawer.active");
    if (!drawer) return;

    hidePublicByobCartPropertyNodes();
    void runValidation({ refreshInventory: true }).then((issues) => {
      if (!issues.length) {
        clearAlert();
        return;
      }
      restoreAlertIfNeeded();
    });
  });

  const drawer = document.querySelector("cart-drawer");
  if (drawer) {
    const observer = new MutationObserver(() => {
      hidePublicByobCartPropertyNodes();
      if (drawer.classList.contains("active")) {
        void runValidation({ refreshInventory: true }).then((issues) => {
          if (!issues.length) clearAlert();
          else restoreAlertIfNeeded();
        });
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });
  }

  document.addEventListener("nextsky:cart:added", () => {
    hidePublicByobCartPropertyNodes();
    window.setTimeout(() => {
      hidePublicByobCartPropertyNodes();
      void runValidation();
    }, 0);
  });
}

init();
